import { supabase } from "./supabaseClient";
import { buildXPaymentHeader, hasWallet } from "./x402Client";

export type PayMethod = 'KHQR' | 'X402';

export type ReleaseFormat = 'VISUAL' | 'JSON';

export interface DashboardOffer {
  survey_id: string;
  title: any;
  for_sale: boolean;
  published: boolean;
  access: 'FREE' | 'PAID';
  formats: ReleaseFormat[];
  price: number | null;
  currency: string;
  methods: PayMethod[];
  is_owner: boolean;
  already_paid: boolean;
  khqr: { account: string; merchant: string; city: string } | null;
  x402_wallet: string | null;
}

export interface PublishConfig {
  access: 'FREE' | 'PAID';
  formats: ReleaseFormat[];
  price?: number | null;
  currency?: string;
  methods?: PayMethod[];
}

export interface SellerPaymentProfile {
  khqr_account_id?: string | null;
  khqr_merchant_name?: string | null;
  khqr_city?: string | null;
  x402_wallet?: string | null;
}

export interface DashboardPurchase {
  id: string; survey_id: string; buyer_id: string; amount: number | null;
  currency: string | null; method: PayMethod; status: string; payment_ref: string | null;
  created_at: string; paid_at: string | null;
}

export async function getDashboardOffer(surveyId: string): Promise<DashboardOffer> {
  const { data, error } = await supabase.rpc('dashboard_offer', { p_survey_id: surveyId });
  if (error) throw error;
  return data as DashboardOffer;
}

// Publish (or update) a dashboard release. Charges credits on first publish.
// Returns { ok, charged, ... } or { ok:false, reason:'INSUFFICIENT_CREDITS', needed, available }.
export async function publishDashboard(surveyId: string, cfg: PublishConfig): Promise<any> {
  const { data, error } = await supabase.rpc('publish_dashboard', {
    p_survey_id: surveyId,
    p_access: cfg.access,
    p_formats: cfg.formats,
    p_price: cfg.access === 'PAID' ? (cfg.price ?? null) : null,
    p_currency: cfg.currency ?? 'USD',
    p_methods: cfg.access === 'PAID' ? (cfg.methods ?? []) : [],
  });
  if (error) throw error;
  return data;
}

export async function unpublishDashboard(surveyId: string): Promise<void> {
  const { error } = await supabase.rpc('unpublish_dashboard', { p_survey_id: surveyId });
  if (error) throw error;
}

// Full machine-readable JSON export of the dashboard (gated server-side).
export async function getDashboardJson(surveyId: string): Promise<any> {
  const { data, error } = await supabase.rpc('dashboard_json', { p_survey_id: surveyId });
  if (error) throw error;
  return data;
}

export async function startKhqrPurchase(surveyId: string): Promise<string> {
  const { data, error } = await supabase.rpc('start_khqr_purchase', { p_survey_id: surveyId });
  if (error) throw error;
  return data as string; // purchase id
}

export async function confirmDashboardPurchase(purchaseId: string, ref: string): Promise<void> {
  const { error } = await supabase.rpc('confirm_dashboard_purchase', { p_purchase_id: purchaseId, p_payment_ref: ref });
  if (error) throw error;
}

export async function cancelDashboardPurchase(purchaseId: string): Promise<void> {
  const { error } = await supabase.rpc('cancel_dashboard_purchase', { p_purchase_id: purchaseId });
  if (error) throw error;
}

// Seller-facing: list purchases of one of my surveys (RLS-gated to my surveys).
export async function listDashboardSales(surveyId: string): Promise<DashboardPurchase[]> {
  const { data, error } = await supabase.from('dashboard_purchases')
    .select('*').eq('survey_id', surveyId).order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as DashboardPurchase[];
}

export async function getPaymentProfile(): Promise<SellerPaymentProfile | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data, error } = await supabase.from('seller_payment_profiles')
    .select('khqr_account_id, khqr_merchant_name, khqr_city, x402_wallet').eq('user_id', user.id).maybeSingle();
  if (error) throw error;
  return data;
}

export async function savePaymentProfile(p: SellerPaymentProfile): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  const { error } = await supabase.from('seller_payment_profiles')
    .upsert({ user_id: user.id, ...p, updated_at: new Date().toISOString() });
  if (error) throw error;
}

// Pay with x402 from an injected wallet. Returns when access is granted, or throws.
export async function payDashboardWithX402(surveyId: string): Promise<void> {
  if (!hasWallet()) throw new Error('No crypto wallet detected. Use KHQR, or install a Base-compatible wallet.');

  // 1. Ask the endpoint for payment requirements (expect HTTP 402).
  const first = await supabase.functions.invoke('purchase-dashboard', { body: { surveyId } });
  if (!first.error) return; // already granted (e.g. previously paid)

  let requirements: any = null;
  try { requirements = await (first.error as any).context?.json?.(); } catch { /* ignore */ }
  if (!requirements?.accepts?.[0]) {
    throw new Error(requirements?.error || 'Could not start the x402 payment.');
  }

  // 2. Sign the USDC authorization and resend with the X-PAYMENT header.
  const header = await buildXPaymentHeader(requirements.accepts[0]);
  const second = await supabase.functions.invoke('purchase-dashboard', {
    body: { surveyId }, headers: { 'X-PAYMENT': header },
  });
  if (second.error) {
    let msg = 'Payment failed.';
    try { msg = (await (second.error as any).context?.json?.())?.error || msg; } catch { /* ignore */ }
    throw new Error(msg);
  }
}
